import { useCallback, useEffect, useMemo, useState } from 'react';

import { warehouseApi, type WarehouseApiError } from '../api/warehouse.api';
import { STORAGE_CATEGORY_LABEL, STORAGE_TYPE_LABEL } from '../status';
import type {
  StorageCategory,
  StorageStatus,
  StorageType,
  WarehouseStorage,
} from '../types';
import { useLoad } from './useLoad';
import { type PagedPicker, usePagedPicker } from './usePagedPicker';

/** Type of the storage's location (SECTION / BIN inherit it). */
export const storageTypeOf = (s: WarehouseStorage): StorageType =>
  s.location?.type ?? s.type ?? 'STORAGE';

/**
 * "Zona B / Rak 1 / Slot 1" — names from the location down to the storage.
 * Names repeat across siblings, so a lineage entry is the storage itself by
 * id, never by name.
 */
export const storagePathOf = (s: WarehouseStorage): string =>
  [
    ...[s.location, s.section]
      .filter((x) => x && x.id !== s.id)
      .map((x) => x?.name ?? ''),
    s.name,
  ].join(' / ');

/**
 * `Location / Section / Name (Level, Type)` — the level always, the type only
 * when it isn't plain Storage (Quarantine, Staging).
 */
export const storageLabel = (s: WarehouseStorage): string => {
  const type = storageTypeOf(s);
  const tags = [
    STORAGE_CATEGORY_LABEL[s.category],
    type !== 'STORAGE' ? STORAGE_TYPE_LABEL[type] : null,
  ].filter(Boolean);
  return `${storagePathOf(s)} (${tags.join(', ')})`;
};

/** Unique, sorted, non-empty ids — a stable request / dependency key. */
const idKey = (ids: readonly string[]): string =>
  Array.from(new Set(ids.filter(Boolean)))
    .sort()
    .join(',');
const idsOf = (key: string): string[] => (key ? key.split(',') : []);

/* ------------------------------- labels --------------------------------- */

export interface StorageLabels {
  /** Every storage this form knows: the ones resolved by `ids=` and remembered. */
  byID: ReadonlyMap<string, WarehouseStorage>;
  /** Keep rows the caller already holds (picked / default) — no `ids=` for them. */
  remember: (rows: readonly WarehouseStorage[]) => void;
  /** Ids the server answered without (no such storage) — not asked again. */
  missing: ReadonlySet<string>;
  /** Some chosen id is still being resolved. */
  loading: boolean;
  /** The resolve request failed — the labels are unknown, not missing. */
  error: WarehouseApiError | null;
  reload: () => void;
}

/**
 * Labels of storages chosen by id — without loading the hub: the ids nobody
 * has described yet go to the server once, in one `ids=` request (any status:
 * an inactive storage still has a name), and the answers are kept. Rows the
 * caller already holds go in through `remember`. Ids the server doesn't know
 * are not asked again.
 */
export function useStorageLabels(
  hubID: number | null,
  ids: readonly string[],
  enabled = true
): StorageLabels {
  const on = enabled && !!hubID;
  const [known, setKnown] = useState<ReadonlyMap<string, WarehouseStorage>>(
    () => new Map()
  );
  const [missing, setMissing] = useState<ReadonlySet<string>>(() => new Set());
  // Both belong to one hub — reset when it changes.
  useEffect(() => {
    setKnown((prev) => (prev.size ? new Map() : prev));
    setMissing((prev) => (prev.size ? new Set() : prev));
  }, [hubID]);

  const unresolvedKey = idKey(
    idsOf(idKey(ids)).filter((id) => !known.has(id) && !missing.has(id))
  );
  const load = useLoad(
    async (signal) => {
      const requested = idsOf(unresolvedKey);
      const found = await warehouseApi.getStoragesByIds(
        hubID as number,
        requested,
        { signal }
      );
      return { hubID, requested, found };
    },
    [hubID, unresolvedKey],
    on && !!unresolvedKey
  );

  useEffect(() => {
    const res = load.data;
    if (!res || res.hubID !== hubID) return;
    if (res.found.length)
      setKnown((prev) => {
        const next = new Map(prev);
        res.found.forEach((s) => next.set(s.id, s));
        return next;
      });
    const got = new Set(res.found.map((s) => s.id));
    const gone = res.requested.filter((id) => !got.has(id));
    if (gone.length)
      setMissing((prev) => new Set([...Array.from(prev), ...gone]));
  }, [load.data, hubID]);

  const remember = useCallback((rows: readonly WarehouseStorage[]) => {
    if (!rows.length) return;
    setKnown((prev) => {
      const next = new Map(prev);
      rows.forEach((s) => next.set(s.id, s));
      return next;
    });
  }, []);

  return useMemo(
    () => ({
      byID: known,
      missing,
      remember,
      loading: load.loading,
      error: on && unresolvedKey ? load.error : null,
      reload: load.reload,
    }),
    [
      known,
      missing,
      remember,
      load.loading,
      load.error,
      load.reload,
      on,
      unresolvedKey,
    ]
  );
}

/* ------------------------------- picker --------------------------------- */

export type StoragePicker = PagedPicker<WarehouseStorage>;

const storageKey = (s: WarehouseStorage) => s.id;

/**
 * One storage picker's server-side list of a hub (see `usePagedPicker`):
 * `category` narrows it to one level, `parentID` to the direct children of one
 * storage (a Location's Sections) — each combination is its own list.
 */
export function useStoragePicker({
  hubID,
  status,
  category,
  parentID,
  enabled,
}: {
  hubID: number | null;
  status: StorageStatus | null;
  category?: StorageCategory;
  parentID?: string;
  enabled: boolean;
}): StoragePicker {
  return usePagedPicker<WarehouseStorage>({
    scope: [hubID, status, category, parentID].map((x) => x ?? '').join('|'),
    enabled: enabled && !!hubID,
    keyOf: storageKey,
    fetchPage: ({ search, page, limit }, signal) =>
      warehouseApi.listStoragesPage(
        {
          hubID: hubID as number,
          status,
          ...(category ? { category } : {}),
          ...(parentID ? { parentID } : {}),
          search,
          page,
          limit,
        },
        { signal }
      ),
  });
}
